import React from 'react';
import { Chip, styled } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ClaimStage } from '../../../interfaces/billing';
import { isActiveStatus, isCompletedStatus, isErrorStatus } from './ProcessStatusIndicator';

const StyledChip = styled(Chip)<{ statusType: 'active' | 'completed' | 'error' | 'default' }>(
  ({ theme, statusType }) => ({
    height: '20px',
    fontSize: '0.75rem',
    fontWeight: 600,
    borderRadius: '10px',
    ...(statusType === 'active' && {
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
      color: theme.palette.primary.main,
      border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
    }),
    ...(statusType === 'completed' && {
      backgroundColor: alpha(theme.palette.success.main, 0.1),
      color: theme.palette.success.main,
      border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
    }),
    ...(statusType === 'error' && {
      backgroundColor: alpha(theme.palette.error.main, 0.1),
      color: theme.palette.error.main,
      border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
    }),
    ...(statusType === 'default' && {
      backgroundColor: alpha(theme.palette.grey[500], 0.1),
      color: theme.palette.grey[600],
      border: `1px solid ${alpha(theme.palette.grey[500], 0.3)}`,
    }),
  })
);

interface ProcessStatusBadgeProps {
  status: ClaimStage;
  label?: string;
  size?: 'small' | 'medium';
  variant?: 'filled' | 'outlined';
  onClick?: () => void;
  className?: string;
}

/**
 * Compact status badge component for process lists
 */
export const ProcessStatusBadge: React.FC<ProcessStatusBadgeProps> = ({
  status,
  label,
  size = 'small',
  variant = 'filled',
  onClick,
  className,
}) => {

  const getStatusConfig = () => {
    switch (status) {
      case ClaimStage.CreationInProgress:
        return {
          type: 'active' as const,
          label: 'Creating',
          pulseAnimation: true,
        };
      case ClaimStage.Created:
        return {
          type: 'completed' as const,
          label: 'Created',
          pulseAnimation: false,
        };
      case ClaimStage.ExportInProgress:
        return {
          type: 'active' as const,
          label: 'Exporting',
          pulseAnimation: true,
        };
      case ClaimStage.Exported:
        return {
          type: 'completed' as const,
          label: 'Exported',
          pulseAnimation: false,
        };
      case ClaimStage.Discarded:
        return {
          type: 'error' as const,
          label: 'Cancelled',
          pulseAnimation: false,
        };
      default:
        return {
          type: 'default' as const,
          label: 'Unknown',
          pulseAnimation: false,
        };
    }
  };

  const statusConfig = getStatusConfig();
  const displayLabel = label || statusConfig.label;

  const PulsingChip = styled(StyledChip)<{ shouldPulse: boolean }>(({ shouldPulse }) => ({
    ...(shouldPulse && {
      animation: 'pulse 2s infinite',
    }),
    '@keyframes pulse': {
      '0%': {
        opacity: 1,
      },
      '50%': {
        opacity: 0.7,
      },
      '100%': {
        opacity: 1,
      },
    },
  }));

  return (
    <PulsingChip
      label={displayLabel}
      statusType={statusConfig.type}
      shouldPulse={statusConfig.pulseAnimation}
      size={size}
      variant={variant}
      onClick={onClick}
      className={className}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        ...(onClick && {
          '&:hover': {
            transform: 'scale(1.05)',
            transition: 'transform 0.2s ease-in-out',
          },
        }),
      }}
    />
  );
};

/**
 * Status badge specifically for process lists with additional metadata
 */
export const ProcessListStatusBadge: React.FC<ProcessStatusBadgeProps & {
  processId: number;
  startedByUser?: string;
  lastModified?: string;
}> = ({ processId, startedByUser, lastModified, ...props }) => {
  const formatTooltip = () => {
    const parts = [`Process #${processId}`];
    if (startedByUser) {
      parts.push(`Started by: ${startedByUser}`);
    }
    if (lastModified) {
      parts.push(`Last modified: ${new Date(lastModified).toLocaleString()}`);
    }
    return parts.join('\n');
  };

  const statusType = (() => {
    const { status } = props;
    if (isActiveStatus(status)) return 'active';
    if (isCompletedStatus(status)) return 'completed';
    if (isErrorStatus(status)) return 'error';
    return 'default';
  })();

  const PulsingChip = styled(StyledChip)<{ shouldPulse: boolean }>(({ shouldPulse }) => ({
    ...(shouldPulse && {
      animation: 'pulse 2s infinite',
    }),
    '@keyframes pulse': {
      '0%': {
        opacity: 1,
      },
      '50%': {
        opacity: 0.7,
      },
      '100%': {
        opacity: 1,
      },
    },
  }));

  return (
    <PulsingChip
      label={props.label}
      size={props.size}
      variant={props.variant}
      onClick={props.onClick}
      className={props.className}
      statusType={statusType}
      shouldPulse={isActiveStatus(props.status)}
      title={formatTooltip()}
    />
  );
};

/**
 * Utility function to get all available status badge configurations
 */
export const getStatusBadgeConfigs = () => {
  return [
    { status: ClaimStage.CreationInProgress, label: 'Creating', type: 'active' },
    { status: ClaimStage.Created, label: 'Created', type: 'completed' },
    { status: ClaimStage.ExportInProgress, label: 'Exporting', type: 'active' },
    { status: ClaimStage.Exported, label: 'Exported', type: 'completed' },
    { status: ClaimStage.Discarded, label: 'Cancelled', type: 'error' },
  ];
};

export default ProcessStatusBadge;